import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@servicetim/ui/components';
import type { CSSProperties } from 'react';

export interface BrandingPreviewProps {
  tenantName: string;
  productName?: string;
  portalName?: string;
  logoUrl?: string | null;
  primaryColor?: string;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/**
 * Live preview of `tenant.settings.branding`. The primary color is injected as the
 * `--color-brand-*` tokens on the preview root, so the same Tailwind utilities the app uses
 * (`bg-brand-600`, `text-accent`, …) render in the tenant's color inside this subtree only.
 */
export function BrandingPreview({
  tenantName,
  productName,
  portalName,
  logoUrl,
  primaryColor,
}: BrandingPreviewProps) {
  const color = primaryColor && HEX.test(primaryColor) ? primaryColor : undefined;
  const tokens = color
    ? ({
        '--color-brand-50': `${color}14`,
        '--color-brand-100': `${color}26`,
        '--color-brand-500': color,
        '--color-brand-600': color,
        '--color-brand-700': color,
      } as CSSProperties)
    : undefined;
  const product = productName?.trim() || tenantName;
  const portal = portalName?.trim() || `${tenantName} help center`;

  return (
    <Card data-slot="branding-preview" style={tokens}>
      <CardHeader>
        <CardTitle>Preview</CardTitle>
        <CardDescription>
          How the admin app, the portal and emails will pick up the branding.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-hidden rounded-lg border border-border">
          <div className="flex h-12 items-center gap-2 border-border border-b bg-surface-raised px-3">
            <Logo logoUrl={logoUrl} name={product} />
            <span className="truncate font-semibold text-fg text-sm">{product}</span>
            <span className="ml-auto rounded-full bg-accent-soft px-2 py-0.5 font-medium text-accent text-xs">
              Admin
            </span>
          </div>
          <div className="space-y-3 bg-surface-sunken p-4">
            <p className="font-semibold text-lg text-fg">{portal}</p>
            <p className="text-fg-muted text-sm">
              Search the knowledge base or raise a request. Your team answers within the agreed
              service level.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex h-9 items-center rounded-md bg-brand-600 px-4 font-medium text-sm text-white">
                Raise a request
              </span>
              <span className="text-accent text-sm underline-offset-4">Browse articles</span>
            </div>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-2 text-xs">
          <dt className="text-fg-muted">Primary color</dt>
          <dd className="flex items-center gap-2 font-mono text-fg-muted">
            <span
              aria-hidden="true"
              className="size-3 rounded-full border border-border-strong"
              style={{ backgroundColor: color ?? 'transparent' }}
            />
            {color ?? 'default'}
          </dd>
          <dt className="text-fg-muted">Logo</dt>
          <dd className="truncate text-fg-muted">{logoUrl || 'none (initial shown)'}</dd>
        </dl>
      </CardContent>
    </Card>
  );
}

function Logo({ logoUrl, name }: { logoUrl?: string | null; name: string }) {
  if (logoUrl) {
    return <img src={logoUrl} alt="" className="size-8 rounded-md object-contain" />;
  }
  return (
    <span
      aria-hidden="true"
      className="grid size-8 place-items-center rounded-md bg-brand-600 font-bold text-sm text-white"
    >
      {name.charAt(0).toUpperCase() || 'S'}
    </span>
  );
}
